import {IServiceFactory} from "../../../service-factory.interface";
import {ILoginCredentials, ILoginWithFormOptions, IUserModel} from "../user-model.interface";
import {IDotRezUserSession} from "../../../dot-rez-api/session/user-session/dot-rez-user.session.interface";
import {DotRezUserSession} from "../../../dot-rez-api/session/user-session/dot-rez-user.session";
import {makeObservable, observable, runInAction} from "mobx";
import {IDotRezBookingSession} from "../../../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";
import {AnonymousUserModel} from "../anonymous-user/anonymous-user.model";
import {AirlineWebapiErrorCodesEnum} from "../../../airline-webapi/airline-webapi-error-codes.enum";
import { AuthorizedUserProfileModel } from "./profile/authorized-user-profile.model";
import {UserRefreshTokenStorage} from "../../user-refresh-token-storage";
import {ChangePasswordFormModel} from "../change-password/change-password-form.model";
import {ILoginResponse} from "../../../airline-webapi/responses/login.response";
import {ICreateBookingSessionOptions, ICreateFlightSearchSessionOptions} from "../../user.service.interface";



interface AuthorizedUserModelOptions {
    refreshTokenStorage: UserRefreshTokenStorage;
    dotRezToken?: string;
}

export class AuthorizedUserModel implements IUserModel {
    constructor(public readonly services: IServiceFactory,
                private readonly options: AuthorizedUserModelOptions) {


        if(this.options.dotRezToken) {
            this._getSessionPromise = Promise.resolve(new DotRezUserSession(this.options.dotRezToken, services));
        } else {
            this._getSessionPromise = this._createUserSession();
        }

        makeObservable<this, '_getSessionPromise'>(this, {
            _getSessionPromise: observable.ref
        });

        this.profile = new AuthorizedUserProfileModel(this);
    }

    readonly profile: AuthorizedUserProfileModel;

    get refreshToken(): string {
        return this.options.refreshTokenStorage.getRefreshToken();
    }

    get rememberMe(): boolean {
        return this.options.refreshTokenStorage.hasRememberMe;
    }

    get isAuthorized(): boolean {
        return true;
    }

    updateRefreshToken(newRefreshToken: string): void {
        this.options.refreshTokenStorage.setRefreshToken(newRefreshToken);
    }

    private _getSessionPromise: Promise<IDotRezUserSession>;
    getSession(): Promise<IDotRezUserSession> {
        return this._getSessionPromise;
    }

    async loginWithCredentials(credentials: ILoginCredentials): Promise<IUserModel> {
        return this;
    }

    async loginWithForm(options: ILoginWithFormOptions): Promise<IUserModel> {
        return this;
    }

    async logout(): Promise<IUserModel> {
        this.options.refreshTokenStorage.removeRefreshToken();
        return new AnonymousUserModel(this.services);
    }

    private async _createDotRezToken(): Promise<string> {
        const response = await this.services.airlineWebapi.createAuthorizedDotRezToken({
            refreshToken: this.refreshToken
        });

        if(response.errorCode === AirlineWebapiErrorCodesEnum.InvalidCredentials) {
            await this.services.user.logout();
        }

        response.ensureSuccess();

        if(!response.data.dotRezToken) {
            //This should never happen, but it makes the compiler happy
            throw new Error('Failed to get dotRez token');
        }

        return response.data.dotRezToken;
    }

    private async _createUserSession(): Promise<IDotRezUserSession> {
        const token = await this._createDotRezToken();

        return new DotRezUserSession(token, this.services)
    }

    reCreateSession(): Promise<IDotRezUserSession> {
        const promise = this._createUserSession();
        runInAction(() => {
            this._getSessionPromise = promise;
            promise.then(() => {
                if(!this.profile.isProfileInitialized) {
                    return this.profile.loadProfile();
                }
            });
        });
        return promise;
    }

    async createBookingSession(options: ICreateBookingSessionOptions): Promise<IDotRezBookingSession> {
        const token = await this._createDotRezToken();

        return this.services.dotRezApi.createBookingSession(token);
    }

    async createFlightSearchSession(options: ICreateFlightSearchSessionOptions): Promise<IDotRezBookingSession> {
        const token = await this._createDotRezToken();

        return this.services.dotRezApi.createFlightSearchSession(token);
    }

    async createTransientBookingSession(): Promise<IDotRezBookingSession> {
        const token = await this._createDotRezToken();
        return this.services.dotRezApi.createTransientBookingSession(token);
    }

    dispose(): void {
        this.profile.dispose();
        this.getSession().then(session => session.dispose());
    }

    registrationConfirmed(): void {
        //nothing to do here
    }

    changePassword(): Promise<ILoginResponse | null> {
        return new Promise<ILoginResponse | null>( async (resolve, reject) => {
            await this.services.dialogFactory.showChangePassword( {
                form: new ChangePasswordFormModel(this.services, this.refreshToken),
                onResolved: async (changePasswordResult: ILoginResponse | null) => {
                    try {
                        if(!changePasswordResult) {
                            resolve(null);
                            return;
                        }

                        (await this.getSession()).dispose();

                        runInAction(() => {
                            this._getSessionPromise = Promise.resolve(new DotRezUserSession(changePasswordResult.dotRezToken, this.services));
                        })

                        this.updateRefreshToken(changePasswordResult.refreshToken)

                        await this.services.dialogFactory.showSimpleMessageDialog({
                            message: this.services.language.translate("Password successfully changed"),
                            buttonText: this.services.language.translate("OK")
                        });

                        resolve(changePasswordResult);
                    }
                    catch(err) {
                        this.services.logger.error('Failed to change password', err);
                        this.services.alert.showError("Could not change your password");
                        reject(err);
                    }

                }
            });
        })
    }

}
